
const libPictProvider = require('pict-provider');

/**
 * Data Lake catalog provider.
 *
 * The data lake IS the materialized-projections system: each customer's lake is a set of physical lake
 * tables, every one produced either by a one-time IMPORT (a clone of a legacy DB, e.g. walbec's Lastrada)
 * or by a COMPUTED projection (a transformation of Headlight operational data). The durable catalog of
 * those datasets is the `MaterializedProjection` meadow entity on the main /1.0/ API (reads auto-scope to
 * the session's customer); the actual dataset rows are read separately from the PDO service's
 * `/1.0/PrivateDataLake/...` surface (see DatalakeDataset-Provider).
 *
 * This provider loads the catalog, splits it into the two kinds the UI manages distinctly, groups each
 * (clones by source system, computed by pipeline / RefreshGroup), and derives a searchable two-tab rail.
 * All state lives in pict.AppData.DatalakeCatalog; the view repaints declaratively. Pure transforms are
 * module functions exported for unit tests.
 */

// --- Pure helpers (deterministic transforms of plain data; exported for tests) ---

/**
 * Classify a MaterializedProjection row as an imported clone or a computed projection.
 * Imported = a raw load of an external/legacy source (PassthroughClone / Beacon / file imports), durable
 * and never auto-refreshed. Computed = derived from the Headlight API or another lake table (typed ops).
 * @param {object} pRow
 * @returns {'imported'|'computed'}
 */
function classifyKind(pRow)
{
	if (!pRow || typeof(pRow) !== 'object')
	{
		return 'computed';
	}
	if (pRow.IsRaw)
	{
		return 'imported'; // a raw lake table with no projection definition
	}
	const tmpOp = String(pRow.OperationType || '');
	const tmpSourceType = String(pRow.SourceType || '');
	if (tmpOp === 'PassthroughClone' || tmpSourceType === 'Beacon' || tmpSourceType === 'CSV' || tmpSourceType === 'JSON' || tmpSourceType === 'XLSX')
	{
		return 'imported';
	}
	return 'computed';
}

/**
 * Split catalog rows into { imported, computed } arrays.
 * @param {Array<object>} pRows
 * @returns {{ imported: Array<object>, computed: Array<object> }}
 */
function splitByKind(pRows)
{
	const tmpResult = { imported: [], computed: [] };
	if (!Array.isArray(pRows))
	{
		return tmpResult;
	}
	for (const tmpRow of pRows)
	{
		tmpResult[classifyKind(tmpRow)].push(tmpRow);
	}
	return tmpResult;
}

/**
 * The source-system root of an address: the segment before the first '/'. e.g.
 * 'walbec_lastrada/AS_KP_MISCHGUT' -> 'walbec_lastrada'.
 * @param {string} pAddress
 * @returns {string}
 */
function sourceRoot(pAddress)
{
	const tmpAddress = String(pAddress || '');
	const tmpSlash = tmpAddress.indexOf('/');
	return (tmpSlash > 0) ? tmpAddress.slice(0, tmpSlash) : tmpAddress;
}

/**
 * Human-friendly label for a source-system root: 'walbec_lastrada' -> 'Walbec Lastrada'.
 * @param {string} pRoot
 * @returns {string}
 */
function humanizeSourceRoot(pRoot)
{
	const tmpRoot = String(pRoot || 'External source');
	if (!tmpRoot)
	{
		return 'External source';
	}
	return tmpRoot
		.split(/[_\s]+/)
		.filter(Boolean)
		.map((pWord) => pWord.charAt(0).toUpperCase() + pWord.slice(1))
		.join(' ');
}

/**
 * The logical lake-read name: the physical table name with the customer-silo prefix stripped.
 * 'C182_PD_MoistureDaily' -> 'PD_MoistureDaily'. The PDO read endpoints reject a leading C<n>_ and resolve
 * the remainder by ProjectionName / a LK 'C%_<suffix>' fallback, so the single-segment remainder is always
 * resolvable.
 * @param {string} pPhysicalTableName
 * @returns {string}
 */
function deriveLogicalReadName(pPhysicalTableName)
{
	return String(pPhysicalTableName || '').replace(/^C\d+_/, '');
}

/**
 * The at-a-glance status chip for a row.
 * @param {object} pRow
 * @returns {{ Label: string, Class: string, Icon: string }}
 */
function deriveStatusChip(pRow)
{
	const tmpKind = classifyKind(pRow);
	if (tmpKind === 'imported')
	{
		return { Label: 'Imported · read-only', Class: 'is-link is-light', Icon: 'fa-box-archive' };
	}
	const tmpStatus = String((pRow && pRow.LastRefreshStatus) || '');
	if (/^Failed/i.test(tmpStatus))
	{
		return { Label: 'Last run failed', Class: 'is-danger is-light', Icon: 'fa-circle-exclamation' };
	}
	if (/^Skipped/i.test(tmpStatus))
	{
		return { Label: 'No changes', Class: 'is-warning is-light', Icon: 'fa-circle-minus' };
	}
	if (/^Succeeded/i.test(tmpStatus))
	{
		return { Label: (pRow && pRow.RefreshEnabled) ? 'Scheduled · ✓' : '✓ Up to date', Class: 'is-success is-light', Icon: 'fa-circle-check' };
	}
	return (pRow && pRow.RefreshEnabled)
		? { Label: 'Scheduled', Class: 'is-info is-light', Icon: 'fa-clock' }
		: { Label: 'Paused', Class: '', Icon: 'fa-pause' };
}

/**
 * A human cadence label from the refresh schedule fields. Cron wins when set.
 * @param {object} pRow
 * @returns {string}
 */
function deriveCadenceLabel(pRow)
{
	if (!pRow || !pRow.RefreshEnabled)
	{
		return 'Manual';
	}
	if (pRow.RefreshCron)
	{
		return `cron: ${pRow.RefreshCron}`;
	}
	const tmpMinutes = parseInt(pRow.RefreshIntervalMinutes, 10);
	if (!isNaN(tmpMinutes) && tmpMinutes > 0)
	{
		if (tmpMinutes % 1440 === 0) { return `every ${tmpMinutes / 1440}d`; }
		if (tmpMinutes % 60 === 0) { return `every ${tmpMinutes / 60}h`; }
		return `every ${tmpMinutes}m`;
	}
	return 'Scheduled';
}

/**
 * Build the rail groups for the imported tab: one group per source-system root.
 * @param {Array<object>} pRows
 * @param {string} pSearch
 * @returns {Array<{ GroupKey: string, GroupLabel: string, Meta: string, Items: Array<object> }>}
 */
function groupClones(pRows, pSearch)
{
	const tmpGroups = {};
	const tmpOrder = [];
	for (const tmpRow of _filterRows(pRows, pSearch))
	{
		const tmpKey = sourceRoot(tmpRow.SourceAddress) || 'external';
		if (!tmpGroups[tmpKey])
		{
			tmpGroups[tmpKey] = { GroupKey: tmpKey, GroupLabel: humanizeSourceRoot(tmpKey), Items: [] };
			tmpOrder.push(tmpKey);
		}
		tmpGroups[tmpKey].Items.push(_railItem(tmpRow));
	}
	return tmpOrder.map((pKey) =>
	{
		const tmpGroup = tmpGroups[pKey];
		tmpGroup.Meta = `${tmpGroup.Items.length} table${tmpGroup.Items.length === 1 ? '' : 's'} · read-only`;
		return tmpGroup;
	});
}

/**
 * Build the rail groups for the computed tab: one group per RefreshGroup (pipeline), 'Ungrouped' last.
 * @param {Array<object>} pRows
 * @param {string} pSearch
 * @returns {Array<{ GroupKey: string, GroupLabel: string, Meta: string, Items: Array<object> }>}
 */
function groupComputed(pRows, pSearch)
{
	const tmpGroups = {};
	const tmpOrder = [];
	for (const tmpRow of _filterRows(pRows, pSearch))
	{
		const tmpKey = String(tmpRow.RefreshGroup || '') || '__ungrouped';
		if (!tmpGroups[tmpKey])
		{
			tmpGroups[tmpKey] = {
				GroupKey: tmpKey,
				GroupLabel: (tmpKey === '__ungrouped') ? 'Standalone projections' : tmpKey,
				Items: [],
			};
			tmpOrder.push(tmpKey);
		}
		tmpGroups[tmpKey].Items.push(_railItem(tmpRow));
	}
	// Ungrouped sorts last.
	tmpOrder.sort((a, b) => (a === '__ungrouped' ? 1 : 0) - (b === '__ungrouped' ? 1 : 0));
	return tmpOrder.map((pKey) =>
	{
		const tmpGroup = tmpGroups[pKey];
		const tmpPipeline = (pKey !== '__ungrouped');
		tmpGroup.Meta = tmpPipeline
			? `pipeline · ${tmpGroup.Items.length} stage${tmpGroup.Items.length === 1 ? '' : 's'}`
			: `${tmpGroup.Items.length} projection${tmpGroup.Items.length === 1 ? '' : 's'}`;
		return tmpGroup;
	});
}

/** Filter rows by a case-insensitive substring over name + physical table + description. */
function _filterRows(pRows, pSearch)
{
	const tmpRows = Array.isArray(pRows) ? pRows : [];
	const tmpSearch = String(pSearch || '').trim().toLowerCase();
	if (!tmpSearch)
	{
		return tmpRows;
	}
	return tmpRows.filter((pRow) =>
		`${pRow.ProjectionName || ''} ${pRow.PhysicalTableName || ''} ${pRow.Description || ''}`.toLowerCase().includes(tmpSearch));
}

/** A single rail row for a MaterializedProjection: the data-only fields the template renders. */
function _railItem(pRow)
{
	const tmpChip = deriveStatusChip(pRow);
	return {
		Name: pRow.ProjectionName || deriveLogicalReadName(pRow.PhysicalTableName) || `Projection ${pRow.IDMaterializedProjection}`,
		PhysicalTableName: pRow.PhysicalTableName || '',
		Description: pRow.Description || '',
		DescriptionSuffix: pRow.Description ? ` — ${pRow.Description}` : '',
		ChipLabel: tmpChip.Label,
		ChipClass: tmpChip.Class,
		ChipIcon: tmpChip.Icon,
		Cadence: deriveCadenceLabel(pRow),
		// How the dataset viewer opens this item: a registry row by GUID.
		OpenKind: 'guid',
		OpenArg: pRow.GUIDMaterializedProjection,
		GroupKey: sourceRoot(pRow.SourceAddress) || 'external',
		GroupLabel: humanizeImportedGroup(sourceRoot(pRow.SourceAddress) || 'external'),
	};
}

/** Friendly group label for an imported group key (the segment after the C<n>_ silo prefix, or a source root). */
function humanizeImportedGroup(pKey)
{
	const tmpKey = String(pKey || '');
	if (/^Lastrada/i.test(tmpKey)) { return 'Lastrada legacy DB'; }
	if (/lastrada/i.test(tmpKey)) { return 'Lastrada legacy DB'; }
	if (/^SiteManager/i.test(tmpKey) || /sitemanager/i.test(tmpKey)) { return 'SiteManager'; }
	return humanizeSourceRoot(tmpKey);
}

/** A rail row for a RAW lake table (an imported clone table that has no MaterializedProjection row). */
function _rawItem(pPhysicalTableName)
{
	const tmpLogical = deriveLogicalReadName(pPhysicalTableName);
	const tmpSegment = tmpLogical.split('_')[0];
	return {
		Name: tmpLogical,
		PhysicalTableName: pPhysicalTableName,
		Description: '',
		DescriptionSuffix: '',
		ChipLabel: 'Imported · read-only',
		ChipClass: 'is-link is-light',
		ChipIcon: 'fa-box-archive',
		Cadence: '',
		// Raw tables open by physical name (no registry row).
		OpenKind: 'table',
		OpenArg: pPhysicalTableName,
		GroupKey: tmpSegment,
		GroupLabel: humanizeImportedGroup(tmpSegment),
	};
}

/** Build imported-tab rail groups from MP "imported" rows + raw lake-table items, grouped + searched. */
function buildImportedGroups(pMPImportedRows, pRawItems, pSearch)
{
	const tmpItems = (pMPImportedRows || []).map((pRow) => _railItem(pRow)).concat(pRawItems || []);
	const tmpSearch = String(pSearch || '').trim().toLowerCase();
	const tmpFiltered = tmpSearch
		? tmpItems.filter((pItem) => `${pItem.Name} ${pItem.PhysicalTableName} ${pItem.Description || ''}`.toLowerCase().includes(tmpSearch))
		: tmpItems;
	const tmpGroups = {};
	const tmpOrder = [];
	for (const tmpItem of tmpFiltered)
	{
		const tmpKey = tmpItem.GroupKey || 'external';
		if (!tmpGroups[tmpKey])
		{
			tmpGroups[tmpKey] = { GroupKey: tmpKey, GroupLabel: tmpItem.GroupLabel || humanizeImportedGroup(tmpKey), Items: [] };
			tmpOrder.push(tmpKey);
		}
		tmpGroups[tmpKey].Items.push(tmpItem);
	}
	// Largest groups first (the Lastrada clone dominates).
	tmpOrder.sort((a, b) => tmpGroups[b].Items.length - tmpGroups[a].Items.length);
	return tmpOrder.map((pKey) =>
	{
		const tmpGroup = tmpGroups[pKey];
		tmpGroup.Meta = `${tmpGroup.Items.length} table${tmpGroup.Items.length === 1 ? '' : 's'} · read-only`;
		return tmpGroup;
	});
}

class DatalakeCatalogProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	/** @returns {*} The shared HeadlightRestClient registered on the fable. */
	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	render()
	{
		if (this.pict.views.DatalakeCatalog)
		{
			this.pict.views.DatalakeCatalog.render();
		}
	}

	initializeState()
	{
		this.pict.AppData.DatalakeCatalog =
		{
			Loading: false,
			Rows: [],
			RawImported: [],  // raw lake tables (imported clones) not in the MaterializedProjection registry
			ActiveKind: 'computed', // 'imported' | 'computed'
			Search: '',
			Kpis: [],
			RailGroups: [],
			LoadingSlot: [],
			EmptySlot: [],
			ErrorSlot: [],
			// Tab button classes (data, not HTML).
			ImportedTabClass: '',
			ComputedTabClass: 'is-active',
			ImportedCount: 0,
			ComputedCount: 0,
		};
	}

	/**
	 * Load the customer's projection catalog. Reads auto-scope to MyCustomer; the IDCustomer filter is
	 * belt-and-suspenders and also scopes super-users (Administrator reads Allow = all customers).
	 */
	loadAsync()
	{
		if (!this.pict.AppData.DatalakeCatalog)
		{
			this.initializeState();
		}
		const tmpState = this.pict.AppData.DatalakeCatalog;
		tmpState.Loading = true;
		tmpState.ErrorSlot = [];
		this.deriveRenderState();
		this.render();

		const tmpCustomerID = parseInt((this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0, 10);
		const tmpFilter = 'FBV~Deleted~EQ~0' + (tmpCustomerID > 0 ? `~FBV~IDCustomer~EQ~${tmpCustomerID}` : '');
		// Page the MP registry to exhaustion rather than a single `/0/2000` (defensive — it's bounded to dozens per
		// customer today, but this drops the theoretical truncation). The imported-clones half (DataCatalog) is a
		// single un-paginatable inventory blob, so it stays one request.
		this._fetchAllMaterializedProjections(tmpFilter, (pError, pBody) =>
		{
			tmpState.Loading = false;
			if (pError)
			{
				tmpState.Rows = [];
				tmpState.ErrorSlot = [ { Message: `Could not load the data lake catalog: ${pError.message || pError}` } ];
				this.deriveRenderState();
				this.render();
				return;
			}
			tmpState.Rows = Array.isArray(pBody) ? pBody : [];
			// Land on the tab that actually has content (computed by default, else imported).
			const tmpSplit = splitByKind(tmpState.Rows);
			if (tmpSplit.computed.length === 0 && tmpSplit.imported.length > 0)
			{
				tmpState.ActiveKind = 'imported';
			}
			this.deriveRenderState();
			this.render();
			// The imported clones (raw lake tables) aren't in the MP registry — pull them from the DataCatalog.
			this._loadDataCatalog(tmpCustomerID);
		});
	}

	/** Page the MaterializedProjection registry to exhaustion (begin/count) → callback(error, allRows). */
	_fetchAllMaterializedProjections(pFilter, fDone)
	{
		const tmpPageSize = 2000;
		const tmpAll = [];
		const fPage = (pBegin) =>
		{
			this.restClient.getJSON(`MaterializedProjections/FilteredTo/${pFilter}/${pBegin}/${tmpPageSize}`, (pError, pBody) =>
			{
				if (pError) { return fDone(pError, tmpAll); }
				const tmpRows = Array.isArray(pBody) ? pBody : [];
				tmpRows.forEach((pRow) => tmpAll.push(pRow));
				if (tmpRows.length < tmpPageSize) { return fDone(null, tmpAll); }   // last (short) page
				fPage(pBegin + tmpPageSize);
			});
		};
		fPage(0);
	}

	/**
	 * Load the raw lake-table inventory from the DataCatalog and keep the C<id>_ tables that have no
	 * MaterializedProjection row — these are the imported clones (e.g. walbec's ~677 C182_LastradaDB_* tables).
	 */
	_loadDataCatalog(pCustomerID)
	{
		const tmpState = this.pict.AppData.DatalakeCatalog;
		if (!(pCustomerID > 0))
		{
			return;
		}
		this.restClient.getJSON('DataCatalog', (pError, pCatalog) =>
		{
			if (pError || !pCatalog || !pCatalog.Sources || !pCatalog.Sources.lake)
			{
				return; // best-effort; the computed catalog still works without it
			}
			const tmpLake = pCatalog.Sources.lake;
			const tmpDatasets = tmpLake.Datasets || tmpLake.datasets || [];
			const tmpNames = (Array.isArray(tmpDatasets) ? tmpDatasets : Object.keys(tmpDatasets))
				.map((pDataset) => (typeof pDataset === 'string') ? pDataset : (pDataset.Name || pDataset.Address || ''))
				.map((pString) => String(pString).replace(/^lake\//, ''));
			const tmpPrefix = `C${pCustomerID}_`;
			const tmpMPPhysical = {};
			tmpState.Rows.forEach((pRow) => { tmpMPPhysical[pRow.PhysicalTableName] = true; });
			tmpState.RawImported = tmpNames
				.filter((pName) => pName.startsWith(tmpPrefix) && !tmpMPPhysical[pName])
				.map((pName) => _rawItem(pName));
			this.deriveRenderState();
			this.render();
		});
	}

	deriveRenderState()
	{
		if (!this.pict.AppData.DatalakeCatalog)
		{
			this.initializeState();
		}
		const tmpState = this.pict.AppData.DatalakeCatalog;
		const tmpSplit = splitByKind(tmpState.Rows);
		const tmpRawImported = tmpState.RawImported || [];
		const tmpImportedTotal = tmpSplit.imported.length + tmpRawImported.length;
		tmpState.ImportedCount = tmpImportedTotal;
		tmpState.ComputedCount = tmpSplit.computed.length;

		// KPI strip (data only).
		const tmpPipelines = new Set(tmpSplit.computed.map((pRow) => pRow.RefreshGroup).filter(Boolean));
		const tmpFailed = tmpSplit.computed.filter((pRow) => /^Failed/i.test(String(pRow.LastRefreshStatus || ''))).length;
		tmpState.Kpis =
		[
			{ Label: 'Datasets', Value: String(tmpState.Rows.length + tmpRawImported.length), Icon: 'fas fa-database', Accent: 'var(--theme-color-brand-primary, #156dd1)' },
			{ Label: 'Imported', Value: String(tmpImportedTotal), Icon: 'fas fa-box-archive', Accent: 'var(--theme-color-status-info, #2b8aa7)' },
			{ Label: 'Projections', Value: String(tmpSplit.computed.length), Icon: 'fas fa-diagram-project', Accent: 'var(--theme-color-status-success, #3a9b5c)' },
			{ Label: 'Pipelines', Value: String(tmpPipelines.size), Icon: 'fas fa-timeline', Accent: 'var(--theme-color-status-warning, #d08a2c)' },
			{ Label: 'Failed runs', Value: String(tmpFailed), Icon: 'fas fa-circle-exclamation', Accent: 'var(--theme-color-status-error, #cf5b5b)' },
		];

		tmpState.ImportedTabClass = (tmpState.ActiveKind === 'imported') ? 'is-active' : '';
		tmpState.ComputedTabClass = (tmpState.ActiveKind === 'computed') ? 'is-active' : '';

		tmpState.RailGroups = (tmpState.ActiveKind === 'imported')
			? buildImportedGroups(tmpSplit.imported, tmpRawImported, tmpState.Search)
			: groupComputed(tmpSplit.computed, tmpState.Search);

		tmpState.LoadingSlot = tmpState.Loading ? [ {} ] : [];
		const tmpVisibleCount = tmpState.RailGroups.reduce((pSum, pGroup) => pSum + pGroup.Items.length, 0);
		const tmpKindLabel = (tmpState.ActiveKind === 'imported') ? 'imported' : 'computed';
		const tmpEmptyMessage = tmpState.Search
			? `No ${tmpKindLabel} datasets matching "${tmpState.Search}".`
			: (tmpState.ActiveKind === 'computed'
				? 'No computed projections yet. Projections you create from your Headlight data will appear here.'
				: 'No imported datasets. Cloned copies of source systems will appear here.');
		tmpState.EmptySlot = (!tmpState.Loading && tmpVisibleCount === 0) ? [ { Message: tmpEmptyMessage } ] : [];
	}

	// --- Interactions ---

	/** Switch the catalog kind via a stable route (#/Datalake/Kind/<kind>) so it lives in the URL. */
	navigateKind(pKind)
	{
		if ((typeof window !== 'undefined') && window.location) { window.location.hash = `#/Datalake/Kind/${ pKind }`; }
		else { this.setKind(pKind); }
	}

	setKind(pKind)
	{
		const tmpState = this.pict.AppData.DatalakeCatalog;
		tmpState.ActiveKind = (pKind === 'imported') ? 'imported' : 'computed';
		this.deriveRenderState();
		this.render();
	}

	setSearch(pValue)
	{
		const tmpState = this.pict.AppData.DatalakeCatalog;
		tmpState.Search = pValue || '';
		this.deriveRenderState();
		// Repaint ONLY the results box, not the whole view — a full render() would rebuild the search
		// <input> (a sibling in .dlc-toolbar) and drop focus/caret after each keystroke. Search affects
		// only RailGroups/EmptySlot/LoadingSlot (all inside #Datalake-Results); the KPIs and tab counts
		// are computed from the full set, so they don't change with the term.
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || tmpCA.getElement('#Datalake-Results').length === 0) { this.render(); return; }
		tmpCA.assignContent('#Datalake-Results', this.pict.parseTemplateByHash('Datalake-Results', tmpState));
	}

	/** Open a dataset's detail view by GUID (the dataset route resolves it). */
	selectDataset(pGUID)
	{
		if (pGUID)
		{
			window.location.hash = `#/Datalake/Dataset/${pGUID}`;
		}
	}

	/** Open a catalog item: a registry projection by GUID, or a raw imported lake table by physical name. */
	openItem(pKind, pArg)
	{
		if (!pArg)
		{
			return;
		}
		window.location.hash = (pKind === 'table')
			? `#/Datalake/Table/${encodeURIComponent(pArg)}`
			: `#/Datalake/Dataset/${pArg}`;
	}

	/** Launch the create-projection wizard. */
	newProjection()
	{
		window.location.hash = '#/Datalake/New';
	}
}

module.exports = DatalakeCatalogProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'DatalakeCatalogProvider',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
};

// Pure helpers exported for unit tests.
module.exports.classifyKind = classifyKind;
module.exports.splitByKind = splitByKind;
module.exports.sourceRoot = sourceRoot;
module.exports.humanizeSourceRoot = humanizeSourceRoot;
module.exports.deriveLogicalReadName = deriveLogicalReadName;
module.exports.deriveStatusChip = deriveStatusChip;
module.exports.deriveCadenceLabel = deriveCadenceLabel;
module.exports.groupClones = groupClones;
module.exports.groupComputed = groupComputed;
